/**
 * Core StatusDot Component
 *
 * Inline status indicator dot with optional label. Used in tables, lists, cards.
 *
 * @example
 *   <StatusDot status="online" />
 *   <StatusDot status="error" label="Disconnected" />
 *   <StatusDot status="warning" label="Degraded" pulse />
 */
import React from 'react';
import { cn } from '@/lib/utils';

export interface StatusDotProps {
  status: 'online' | 'offline' | 'error' | 'warning' | 'pending' | 'info' | 'idle';
  label?: string;
  /** Pulsing animation for active states */
  pulse?: boolean;
  /** Size */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const dotColors: Record<string, string> = {
  online: 'bg-emerald-500',
  offline: 'bg-slate-400',
  error: 'bg-red-500',
  warning: 'bg-amber-500',
  pending: 'bg-blue-400',
  info: 'bg-blue-500',
  idle: 'bg-slate-300',
};

const sizeMap = { sm: 'h-1.5 w-1.5', md: 'h-2 w-2', lg: 'h-2.5 w-2.5' };

export const StatusDot: React.FC<StatusDotProps> = ({
  status,
  label,
  pulse = false,
  size = 'md',
  className = '',
}) => (
  <span className={cn('inline-flex items-center gap-1.5', className)}>
    <span className={cn('rounded-full shrink-0', sizeMap[size], dotColors[status], pulse && 'animate-pulse')} />
    {label && <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>}
  </span>
);
export default StatusDot;
